import { useQuery } from '@tanstack/react-query'
import { getEtlRun, getEtlRuns } from '../services/etlRunsApi'
import type { EtlRunOutcome, EtlRunQuery } from '../types/etlRuns'
import { FREQUENT, LIVE } from '../utils/polling'

/**
 * Entity runs, newest first. Polls because the list's newest entries are
 * exactly the ones still moving.
 *
 * The query is in the key by value, and getEtlRuns is called through a closure,
 * for the reason useConnections gives: the key and the argument must move
 * together, or a filter change would serve the previous filter's cached list.
 */
export function useEtlRuns(query: EtlRunQuery = {}) {
  return useQuery({
    queryKey: ['observatory', 'etlRuns', query],
    queryFn: () => getEtlRuns(query),
    refetchInterval: FREQUENT,
  })
}

/**
 * How often one run is re-asked, by its outcome.
 *
 * A live run moves every few seconds — its counters, its chunk phase, its
 * heartbeat — so it polls on LIVE. A stale run is not moving, but it is the one
 * outcome that can still change without anyone touching it: a resume reopens it
 * and its heartbeat comes back, so it is watched on FREQUENT. Anything terminal
 * does not change, and nor does a row nobody has started; neither is polled.
 */
export function etlRunRefetchInterval(outcome: EtlRunOutcome | undefined): number | false {
  if (outcome === 'running') return LIVE
  if (outcome === 'stale') return FREQUENT
  return false
}

/**
 * One entity run.
 *
 * Shares its key with the older hooks/useOverview.ts useEtlRun over the same
 * endpoint; this payload is a superset of the one that hook's type declares, so
 * the two read the same cache entry safely until that hook is deleted.
 */
export function useEtlRun(runId: string) {
  return useQuery({
    queryKey: ['observatory', 'etlRun', runId],
    queryFn: () => getEtlRun(runId),
    enabled: Boolean(runId),
    refetchInterval: (query) => etlRunRefetchInterval(query.state.data?.outcome),
  })
}
